// Carrier library REST client — FE-SCRM-030.
//
// Talks to BE-SCRM-030's ``/api/v1/carriers/`` surface. The list
// endpoint accepts ``?q=`` (case-insensitive substring on name),
// ``?carrier_type=``, and ``?status=``; the FE folds ``status="all"``
// to "no status param" so the backend's default of "include both
// active and inactive" applies.

import { api, ApiError } from "@/api/client";

import {
  CarrierNotFoundError,
  type CarrierContact,
  type CarrierContactListResponse,
  type CarrierContactWriteInput,
  type CarrierDetail,
  type CarrierListFilters,
  type CarrierListResponse,
  type CarrierTouchpointListResponse,
  type CarrierWriteInput,
} from "./types";


function buildListParams(filters: CarrierListFilters): string {
  const sp = new URLSearchParams();
  if (filters.q && filters.q.trim()) sp.set("q", filters.q.trim());
  if (filters.carrier_type) sp.set("carrier_type", filters.carrier_type);
  if (filters.status && filters.status !== "all") {
    sp.set("status", filters.status);
  }
  return sp.toString();
}


export async function fetchCarrierList(
  filters: CarrierListFilters,
  opts?: { signal?: AbortSignal },
): Promise<CarrierListResponse> {
  const qs = buildListParams(filters);
  const path = `/api/v1/carriers/${qs ? `?${qs}` : ""}`;
  return api.get<CarrierListResponse>(path, opts);
}


export async function fetchCarrierDetail(
  carrierId: string,
  opts?: { signal?: AbortSignal },
): Promise<CarrierDetail> {
  try {
    return await api.get<CarrierDetail>(
      `/api/v1/carriers/${encodeURIComponent(carrierId)}`,
      opts,
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      throw new CarrierNotFoundError(carrierId);
    }
    throw err;
  }
}


export function createCarrier(input: CarrierWriteInput): Promise<CarrierDetail> {
  return api.post<CarrierDetail>("/api/v1/carriers/", input);
}


export function updateCarrier(
  carrierId: string, input: Partial<CarrierWriteInput>,
): Promise<CarrierDetail> {
  return api.patch<CarrierDetail>(
    `/api/v1/carriers/${encodeURIComponent(carrierId)}`, input,
  );
}


export function deleteCarrier(carrierId: string): Promise<{ deleted: boolean }> {
  return api.delete<{ deleted: boolean }>(
    `/api/v1/carriers/${encodeURIComponent(carrierId)}`,
  );
}


export function fetchCarrierContacts(
  carrierId: string,
  opts?: { signal?: AbortSignal },
): Promise<CarrierContactListResponse> {
  return api.get<CarrierContactListResponse>(
    `/api/v1/carriers/${encodeURIComponent(carrierId)}/contacts`, opts,
  );
}


export function createCarrierContact(
  carrierId: string, input: CarrierContactWriteInput,
): Promise<CarrierContact> {
  return api.post<CarrierContact>(
    `/api/v1/carriers/${encodeURIComponent(carrierId)}/contacts`, input,
  );
}


export function updateCarrierContact(
  contactId: string, input: Partial<CarrierContactWriteInput>,
): Promise<CarrierContact> {
  return api.patch<CarrierContact>(
    `/api/v1/carrier-contacts/${encodeURIComponent(contactId)}`, input,
  );
}


export function deleteCarrierContact(
  contactId: string,
): Promise<{ deleted: boolean }> {
  return api.delete<{ deleted: boolean }>(
    `/api/v1/carrier-contacts/${encodeURIComponent(contactId)}`,
  );
}


export function fetchCarrierTouchpoints(
  carrierId: string,
  opts?: { signal?: AbortSignal },
): Promise<CarrierTouchpointListResponse> {
  return api.get<CarrierTouchpointListResponse>(
    `/api/v1/carriers/${encodeURIComponent(carrierId)}/touchpoints`, opts,
  );
}
